import type { TFunction } from 'i18next'
import type {
  ExtractPreviewResponse,
  StandardExtractSection,
} from '@/shared/hooks/api/userStandards'
import { isExtractPreviewSectionNotFound } from '../../lib/extractionPreviewUi'

export const PREVIEW_TITLE_KEYS: Record<StandardExtractSection, string> = {
  scope: 'detail.extractPreview.titleScope',
  significantHazards: 'detail.extractPreview.titleSignificantHazards',
  riskReductionMeasures: 'detail.extractPreview.titleRiskReductionMeasures',
  informationForUse: 'detail.extractPreview.titleInformationForUse',
}

export type ExtractionPreviewUiState = {
  /** Hard failure (PDF unusable or extraction error — cannot save). */
  allFailed: boolean
  hasSectionData: boolean
  /** Section was searched but absent in the PDF; user may save empty JSON. */
  sectionNotFound: boolean
  /** Continue, translate, or save is allowed. */
  canProceed: boolean
  sectionLabel: string
  pdfImpediment: { reason: string } | null
  sectionImpediment: { reason: string } | null
}

export function getExtractionPreviewUiState(
  section: StandardExtractSection,
  preview: ExtractPreviewResponse,
  translate: TFunction<'standardsLibrary'>
): ExtractionPreviewUiState {
  const pdfImpediment = preview.pdfImpediment ?? null
  const scope = preview.scope?.trim() ?? ''
  const hazards = preview.significantHazards?.hazards ?? []
  const clauses = preview.informationForUse?.clauses ?? []
  const rrms = preview.riskReductionMeasures?.measures ?? []

  const scopeImpediment = preview.scopeImpediment ?? null
  const hazardsImpediment = preview.significantHazardsImpediment ?? null
  const ifuImpediment = preview.informationForUseImpediment ?? null
  const rrmsImpediment = preview.riskReductionMeasuresImpediment ?? null

  const sectionNotFound = isExtractPreviewSectionNotFound(section, preview)

  let hasSectionData = false
  let sectionImpediment: { reason: string } | null = null
  let sectionLabel = ''

  if (section === 'scope') {
    hasSectionData = scope.length > 0
    sectionImpediment =
      scopeImpediment?.reason != null
        ? { reason: scopeImpediment.reason }
        : null
    sectionLabel = translate('detail.itehScope')
  } else if (section === 'significantHazards') {
    hasSectionData = hazards.length > 0
    sectionImpediment =
      hazardsImpediment?.reason != null
        ? { reason: hazardsImpediment.reason }
        : null
    sectionLabel = translate('detail.significantHazards')
  } else if (section === 'informationForUse') {
    hasSectionData = clauses.length > 0
    sectionImpediment =
      ifuImpediment?.reason != null ? { reason: ifuImpediment.reason } : null
    sectionLabel = translate('detail.informationForUseRequirements')
  } else if (section === 'riskReductionMeasures') {
    hasSectionData = rrms.length > 0
    sectionImpediment =
      rrmsImpediment?.reason != null ? { reason: rrmsImpediment.reason } : null
    sectionLabel = translate('detail.riskReductionMeasures')
  }

  const allFailed =
    pdfImpediment != null ||
    (!hasSectionData && sectionImpediment != null && !sectionNotFound)

  const canProceed =
    pdfImpediment == null && (hasSectionData || sectionNotFound)

  return {
    allFailed,
    hasSectionData,
    sectionNotFound,
    canProceed,
    sectionLabel,
    pdfImpediment,
    sectionImpediment,
  }
}
